/**
 * Needs you's groups (brief 4.1, ADR 0058 decision 3 as amended): the decisions first, in a fixed
 * order (Security, then the one set of groups every surface uses, ADR 0072: Requests, Questions,
 * Blocked, Assistant, Sessions waiting, Asked), then the problem groups, where the watch's
 * incidents and the derived rows rank together by severity, then the oldest. A problem group with
 * something critical comes before one with only warnings. Pure, so tested.
 */
import type { Incident, IncidentDetector, IncidentSeverity, Risk } from '@orgus/protocol';
import { ASK_GROUP_LABEL, ASK_GROUP_ORDER, compareAsks } from '../approvals/decision.ts';
import type { AskGroup } from '../approvals/decision.ts';

export type NeedGroup = 'security' | AskGroup | 'problems' | 'budget' | 'work' | 'machines';

export const NEED_GROUP_LABEL: Record<NeedGroup, string> = {
  security: 'Security',
  ...ASK_GROUP_LABEL,
  problems: 'Problems',
  budget: 'Budget',
  work: 'Stuck or failed work',
  machines: 'Machines and worktrees',
};

/** Decisions only a person can make, in this order, never reordered. */
export const DECISION_GROUPS: readonly NeedGroup[] = ['security', ...ASK_GROUP_ORDER];
/** Problem groups; this order breaks a tie in severity. */
export const PROBLEM_GROUPS: readonly NeedGroup[] = ['problems', 'budget', 'work', 'machines'];

/** Rows per group before "Show N more". */
export const GROUP_ROWS = 3;

/** The fields grouping and ranking read from a derived row. */
export interface Rankable {
  kind: string;
  key: string;
  verb: string;
  at: number | null;
  /** A pending request's deadline and risk, for the one order inside its group (ADR 0072). */
  order?: { deadline?: number; risk?: Risk };
}

export function needGroupOf(item: Pick<Rankable, 'kind' | 'key' | 'verb'>): NeedGroup {
  switch (item.kind) {
    case 'securityCritical':
    case 'securityWarning':
      return 'security';
    case 'problem-critical':
    case 'problem-warning':
      return 'problems';
    case 'budgetOver':
    case 'budget':
    case 'runaway':
      return 'budget';
    case 'session':
      // Keys say what a row is: b: blocked, q: a held question, plan or form, as: an assistant
      // step, s: a session waiting in its terminal, anything else a pending request.
      if (item.key.startsWith('b:')) return 'blocked';
      if (item.key.startsWith('q:')) return 'questions';
      if (item.key.startsWith('as:')) return 'assistant';
      if (item.key.startsWith('s:')) return 'sessions';
      return 'requests';
    case 'asked':
      return 'asked';
    case 'machine':
    case 'worktree':
      return 'machines';
    default:
      return 'work';
  }
}

/** Where each detector's incidents sit: beside the derived rows about the same kind of thing. */
export const INCIDENT_GROUP: Record<IncidentDetector, NeedGroup> = {
  'refusal-storm': 'problems',
  'count-mismatch': 'problems',
  'hub-drift': 'problems',
  'main-red': 'problems',
  watch: 'problems',
  'landing-failed': 'work',
  'flaky-test': 'work',
  'stuck-session': 'work',
  'stale-failure': 'work',
  'failure-loop': 'work',
  'cold-resumes': 'budget',
  'context-heavy': 'budget',
  'burn-without-progress': 'budget',
  'orphaned-work': 'machines',
  'forgotten-process': 'machines',
  'dev-server': 'machines',
  cleanup: 'machines',
  'disk-low': 'machines',
  'mission-environment': 'machines',
  'loop-stale': 'problems',
  'start-storm': 'problems',
  'worktrees-heavy': 'machines',
  'backup-stale': 'problems',
};

const SEVERITY_RANK: Record<IncidentSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

/** A derived row's weight beside the incidents, on the same scale. */
const KIND_SEVERITY: Record<string, IncidentSeverity> = {
  'problem-critical': 'critical',
  'problem-warning': 'medium',
  budgetOver: 'high',
  runaway: 'high',
  budget: 'medium',
  agent: 'high',
  pipeline: 'high',
  landing: 'high',
  machine: 'medium',
  worktree: 'medium',
};

export function needSeverity(kind: string): IncidentSeverity {
  return KIND_SEVERITY[kind] ?? 'medium';
}

export type NeedsRow<N> = { type: 'need'; key: string; need: N } | { type: 'incident'; key: string; incident: Incident };

export interface NeedsGroup<N> {
  group: NeedGroup;
  rows: NeedsRow<N>[];
}

interface Ranked<N> {
  row: NeedsRow<N>;
  sev: number;
  /** 1 once someone took it (acknowledged, fixing): after the open ones of the same severity. */
  taken: number;
  at: number;
  n: number;
}

const rowKey = (i: Incident) => `i:${i.id}`;

function orderOf<N extends Rankable>(r: NeedsRow<N>): { deadline?: number; risk?: Risk; at: number } {
  if (r.type !== 'need') return { at: r.incident.openedAt };
  return { ...r.need.order, at: r.need.at ?? Number.POSITIVE_INFINITY };
}

function rank<N>(rows: Ranked<N>[]): NeedsRow<N>[] {
  return rows.sort((a, b) => a.sev - b.sev || a.taken - b.taken || a.at - b.at || a.n - b.n).map((r) => r.row);
}

/**
 * The groups to show: decisions as they came (the caller already ordered them), then the problem
 * groups ranked by severity, open before taken, then the oldest; a problem group leads with its
 * worst row.
 */
export function needsGroups<N extends Rankable>(needs: readonly N[], incidents: readonly Incident[]): NeedsGroup<N>[] {
  const by = new Map<NeedGroup, Ranked<N>[]>();
  const add = (g: NeedGroup, r: Ranked<N>) => by.set(g, [...(by.get(g) ?? []), r]);
  let n = 0;
  for (const item of needs) {
    add(needGroupOf(item), { row: { type: 'need', key: item.key, need: item }, sev: SEVERITY_RANK[needSeverity(item.kind)], taken: 0, at: item.at ?? Number.POSITIVE_INFINITY, n: n++ });
  }
  for (const i of incidents) {
    add(INCIDENT_GROUP[i.detector] ?? 'problems', { row: { type: 'incident', key: rowKey(i), incident: i }, sev: SEVERITY_RANK[i.severity], taken: i.state === 'open' ? 0 : 1, at: i.openedAt, n: n++ });
  }
  // Inside a decision group, the one order every surface uses: soonest deadline, then risk, then the longest wait.
  const decisions = DECISION_GROUPS.filter((g) => by.has(g)).map((g) => ({ group: g, rows: g === 'security' ? by.get(g)!.map((r) => r.row) : [...by.get(g)!].sort((x, y) => compareAsks(orderOf(x.row), orderOf(y.row)) || x.n - y.n).map((r) => r.row) }));
  const worst = (g: NeedGroup) => Math.min(...by.get(g)!.map((r) => r.sev));
  const problems = PROBLEM_GROUPS.filter((g) => by.has(g))
    .sort((a, b) => worst(a) - worst(b) || PROBLEM_GROUPS.indexOf(a) - PROBLEM_GROUPS.indexOf(b))
    .map((g) => ({ group: g, rows: rank(by.get(g)!) }));
  return [...decisions, ...problems];
}

/** Every row shown, across the groups: the panel's count. */
export function rowCount(groups: readonly NeedsGroup<unknown>[]): number {
  return groups.reduce((s, g) => s + g.rows.length, 0);
}

/**
 * Where a notice's "View incident" lands (?incident=<id>): its group, its place, and whether the
 * group has to show all its rows for it to be seen. Null when the incident is not shown.
 */
export function linkedRow(groups: readonly NeedsGroup<unknown>[], incidentId: string | null): { group: NeedGroup; index: number; showAll: boolean } | null {
  if (!incidentId) return null;
  for (const g of groups) {
    const index = g.rows.findIndex((r) => r.type === 'incident' && r.incident.id === incidentId);
    if (index >= 0) return { group: g.group, index, showAll: index >= GROUP_ROWS };
  }
  return null;
}
